import { Component, Input, OnInit, OnChanges, SimpleChanges, ViewChild } from "@angular/core";
import { MatDialog, MatDialogRef } from "@angular/material/dialog";
import { ConfirmationComplaintService } from "app/core/cnc/admin/_services/confirmation-complaint.service";
import { ModalAlertComponent } from "../modal-alert/modal-alert.component";
import { PdfViewerComponent, PdfViewerModule } from "ng2-pdf-viewer";
import { DomSanitizer } from "@angular/platform-browser";
import { TruckReceiptService } from "app/core/operations/processing/_services/truck-receipt.service";
import { InvoicingService } from "app/core/o2c/processing";
import { finalize } from "rxjs/operators";
import { Observable } from "rxjs";
/** Invoice option for C&C Select Invoice for Credit mode (dropdown + PDF viewer) */
export interface InvoiceOptionForPdf {
	transactionNumber: number;
	transactionDate: string;
	arOsBalanceCurrency: number;
	displayText: string;
}

/**
 * Pdf viewer dialog. Two usage modes (do not mix):
 * - Default: set only data (and optionally lonNumber, isClaim, etc.); do NOT set invoiceOptions nor isInvoiceSelectionMode.
 * - C&C Select Invoice for Credit: set invoiceOptions + isInvoiceSelectionMode = true, then initInvoiceSelection(). Each dropdown change loads that invoice PDF.
 * @author Castor Transformación Digital - Alvaro Felipe Garcia Mendez
 * @since 2019-12-4
 */
@Component({
  standalone: false,
	selector: "kt-pdf-viewer-dialog",
	templateUrl: "./pdf-viewer-dialog.component.html",
	styleUrls: ["./pdf-viewer-dialog.component.scss"],
})
export class PdfViewerDialogComponent implements OnInit, OnChanges {
	isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
	@ViewChild(PdfViewerComponent) private pdfComponent: PdfViewerComponent;
	private dataGetterSetter: string;

	/** C&C Select Invoice for Credit: list of invoices; each selection loads its PDF in the viewer */
	@Input() public invoiceOptions: InvoiceOptionForPdf[] = [];
	/** When true, shows "Select Invoice for Credit" (dropdown + PDF) or "Create Credit" (enter number only if invoiceOptions empty); closes with { selectedInvoiceNumber } or { enteredCreditNumber } */
	@Input() public isInvoiceSelectionMode: boolean = false;
	/** Value for "Create Credit" mode (invoiceOptions empty); parent can set current value before open */
	public enteredCreditNumberValue: number | null = null;
	/** Loading state when fetching invoice PDF on demand (C&C mode) */
	public pdfLoadingInvoice: boolean = false;
	/** Optional callback for C&C Select Invoice: when set, Submit calls API and shows spinner until response; on error opens a modal. */
	public onSubmitInvoice?: (invoiceNumber: number) => Observable<any>;
	/**
	 * Optional: returns base64 PDF for an invoice number (Observable or Promise). If unset in C&C mode, PDF is loaded via InvoicingService.
	 */
	public getInvoicePdf?: (invoiceNumber: number) => Observable<string> | PromiseLike<string> | unknown;
	/** Loading state when Submit (createCreditNoteFromInvoice) is in progress (C&C mode) */
	public isSubmittingInvoice: boolean = false;

	/**
	 * Receives the coming data (base64 string) to set the pdf
	 */
	@Input() public set data(value: string) {
		this.dataGetterSetter = value;
		this.setPFD(this.dataGetterSetter);
	}

	/**
	 * Getter for incoming data
	 */
	public get data(): string {
		return this.dataGetterSetter;
	}

	/**
	 * Receive the invoice number
	 */
	@Input() public invoiceNumber: string;
	
	private orderNumberGetterSetter: string;

	/**
	 * Receives the order number
	 */
	@Input() public set orderNumber(value: string) {
		this.orderNumberGetterSetter = value;
	}

	/**
	 * Getter for order number
	 */
	public get orderNumber(): string {
		return this.orderNumberGetterSetter;
	}

	/**
	 * Receive the division
	 */
	@Input() public division: string;

	/**
	 * Receive the gemDBKey for generating second home PDF
	 */
	@Input() public gemDBKey: number;

	private invoiceMethodGetterSetter: string;

	/**
	 * Receives the invoice method and triggers second home check
	 * This is set last in showPdf to ensure all other values are available
	 */
	@Input() public set invoiceMethod(value: string) {
		this.invoiceMethodGetterSetter = value;
		if (this.orderNumberGetterSetter) {
			this.checkOrdersWithSecondHome();
		}
	}

	/**
	 * Getter for invoice method
	 */
	public get invoiceMethod(): string {
		return this.invoiceMethodGetterSetter;
	}

	/**
	 * Receive the lon number
	 */
	@Input() public lonNumber: string;

	/**
	 * Receive the edit mode
	 */
	@Input() public isLonEditMode: boolean;

	/**
	 * Indicates if this is a claim PDF (true) or LON PDF (false)
	 */
	@Input() public isClaim: boolean = false;

	/**
	 * When set (non-empty), replaces the default "Claim Number: …" / "Lon Number: …" title (e.g. Transportation Standard Form PDF).
	 */
	@Input() public claimPdfHeaderTitleOverride: string | null = null;

	/**
	 * When true, hides Sent Date, Email TO, and Email CC under the claim/LON header (minimal header: title + PDF only).
	 */
	@Input() public hideClaimPdfMetadata: boolean = false;

	/**
	 * Receive the pdfList
	 */
	@Input() public pdfList: any;

	/**
	 * Receive notification data for Claims
	 */
	@Input() public notificationData: any;

	/**
	 * Creation date for 3PL
	 */
	@Input() public fileName: string;

	/**
	 * Store the path to show the pdf
	 */
	public path: string;

	/**
	 * Store the id of pdf selected
	 */
	public selectedPdfId: number;
	public emailToList: string;
	public emailCcList: string;

	/**
	 * Set the name for the pdf file to download
	 */
	public name: string;

	/**
	 * Status to know when to show second home button
	 */
	public showSecondHomeButton: boolean = false;

	/**
	 * Store the orders with second home data
	 */
	public ordersWithSecondHome: any[] = [];

	renderText = true;
	originalSize = false;
	fitToPage = false;
	showAll = true;
	autoresize = false;
	showBorders = true;
	renderTextModes = [0, 1, 2];
	renderTextMode = 1;
	rotation = 0;
	zoom = 1;
	zoomScale = "page-width";
	zoomScales = ["page-width", "page-fit", "page-height"];
	pdfQuery = "";
	totalPages: number;
	fileUrl;

	zoomIn() {
		this.zoom += 0.05;
	}

	zoomOut() {
		if (this.zoom > 0.05) this.zoom -= 0.05;
	}

	rotateDoc() {
		this.rotation += 90;
	}
	printPDF() {
		window.open(this.path).print();
	}

	searchQueryChanged(newQuery: string) {
		if (newQuery !== this.pdfQuery) {
			this.pdfQuery = newQuery;
			(this.pdfComponent.pdfFindController as any).executeCommand("find", {
				query: this.pdfQuery,
				highlightAll: true,
			});
		} else {
			(this.pdfComponent.pdfFindController as any).executeCommand("findagain", {
				query: this.pdfQuery,
				highlightAll: true,
			});
		}
	}
	constructor(
		private dialogRef: MatDialogRef<any>,
		private dialog: MatDialog,
		private sanitizer: DomSanitizer,
		private truckReceiptService: TruckReceiptService,
		private invoicingService: InvoicingService,
		private confirmationComplaintService: ConfirmationComplaintService
	) {}

	ngOnInit() {}

	ngOnChanges(changes: SimpleChanges): void {
		if (changes["invoiceOptions"] && this.isInvoiceSelectionMode && this.invoiceOptions?.length > 0) {
			this.initInvoiceSelection();
		}
	}

	/** Call after setting invoiceOptions and isInvoiceSelectionMode (e.g. after dialog open). Loads first invoice PDF. */
	public initInvoiceSelection(): void {
		if (this.isInvoiceSelectionMode && this.invoiceOptions?.length > 0) {
			this.selectedPdfId = this.invoiceOptions[0].transactionNumber;
			this.loadInvoicePdfForSelection(this.selectedPdfId);
		}
	}

	/** C&C mode: close with selected invoice number so parent can call createCreditNoteFromInvoice. */
	public closeWithSelection(): void {
		this.dialogRef.close({ selectedInvoiceNumber: this.selectedPdfId });
	}

	/**
	 * C&C Select Invoice for Credit: on Submit, if onSubmitInvoice is set, call API and show spinner until response;
	 * on error show modal; on success close with createResponse. Otherwise close with selected invoice only.
	 */
	public submitSelection(): void {
		if (!this.onSubmitInvoice || this.selectedPdfId == null) {
			this.closeWithSelection();
			return;
		}
		this.isSubmittingInvoice = true;
		const invoiceNumber = this.selectedPdfId;
		const result = this.onSubmitInvoice(invoiceNumber);
		if (result && typeof (result as any).subscribe === "function") {
			(result as any).subscribe({
				next: (createResponse: any) => {
					this.isSubmittingInvoice = false;
					if (this.confirmationComplaintService.isApiEnvelopeError(createResponse)) {
						this.showCreditNoteCreateMessageModal(
							this.confirmationComplaintService.getApiEnvelopeMessage(createResponse)
								|| "Error creating credit note from invoice."
						);
						return;
					}
					this.dialogRef.close({ selectedInvoiceNumber: invoiceNumber, createResponse });
				},
				error: (err: any) => {
					this.isSubmittingInvoice = false;
					const payload = this.confirmationComplaintService.parseHttpFailurePayload(err);
					const msg = payload?.message || "Error creating credit note from invoice.";
					this.showCreditNoteCreateMessageModal(msg);
				}
			});
		} else if (result && typeof (result as any).then === "function") {
			(result as any).then((createResponse: any) => {
				this.isSubmittingInvoice = false;
				if (this.confirmationComplaintService.isApiEnvelopeError(createResponse)) {
					this.showCreditNoteCreateMessageModal(
						this.confirmationComplaintService.getApiEnvelopeMessage(createResponse)
							|| "Error creating credit note from invoice."
					);
					return;
				}
				this.dialogRef.close({ selectedInvoiceNumber: invoiceNumber, createResponse });
			}).catch((err: any) => {
				this.isSubmittingInvoice = false;
				const payload = this.confirmationComplaintService.parseHttpFailurePayload(err);
				const msg = payload?.message || "Error creating credit note from invoice.";
				this.showCreditNoteCreateMessageModal(msg);
			});
		} else {
			this.isSubmittingInvoice = false;
			this.dialogRef.close({ selectedInvoiceNumber: invoiceNumber });
		}
	}

	/** C&C mode (invoiceOptions empty): close with entered credit number if valid. */
	public closeWithEnteredCreditNumber(): void {
		const num = this.enteredCreditNumberValue != null ? Number(this.enteredCreditNumberValue) : NaN;
		if (!isNaN(num) && num > 0) {
			this.dialogRef.close({ enteredCreditNumber: num });
		}
	}

	/** C&C mode: load invoice PDF by transaction number; uses getInvoicePdf if set, else InvoicingService. */
	public loadInvoicePdfForSelection(transactionNumber: number): void {
		if (!transactionNumber) return;
		this.pdfLoadingInvoice = true;
		if (this.path) {
			URL.revokeObjectURL(this.path);
			this.path = null;
			this.dataGetterSetter = null;
		}
		this.fileUrl = null;

		if (this.getInvoicePdf) {
			const result = this.getInvoicePdf(transactionNumber);
			if (result && typeof (result as any).subscribe === "function") {
				(result as any).subscribe({
					next: (base64: string) => {
						this.setPFD(base64);
						this.pdfLoadingInvoice = false;
					},
					error: () => {
						this.pdfLoadingInvoice = false;
					}
				});
			} else if (result && typeof (result as any).then === "function") {
				(result as any).then((base64: string) => {
					this.setPFD(base64);
					this.pdfLoadingInvoice = false;
				}).catch(() => {
					this.pdfLoadingInvoice = false;
				});
			} else {
				this.pdfLoadingInvoice = false;
			}
			return;
		}

		const filters = {
			division: "",
			poNumber: "",
			salesOrder: "",
			invoiceNumber: transactionNumber,
			debitNumber: "",
			creditNumber: "",
			customerNumber: "",
			startDate: "",
			endDate: "",
			status: ""
		};
		this.invoicingService.getInvoiceSearch(filters).pipe(
			finalize(() => { this.pdfLoadingInvoice = false; })
		).subscribe({
			next: (invoiceList: any) => {
				const list = Array.isArray(invoiceList) ? invoiceList : (invoiceList?.data ?? []);
				if (list.length === 0) return;
				const invoice = list[0];
				if (invoice.deliveryMethod === undefined && invoice.customerAddresses?.sendMethod) {
					invoice.deliveryMethod = invoice.customerAddresses.sendMethod;
				}
				if (invoice.statusFlag === "H") invoice.statusFlag = "Hold";
				else if (invoice.statusFlag === "P") invoice.statusFlag = "Processed";
				else if (invoice.statusFlag !== "P" && invoice.statusFlag !== "H") invoice.statusFlag = "Unprocessed";
				const toProcess = false;
				const isRevised = false;
				this.invoicingService.generateInvoice([invoice], toProcess, isRevised).subscribe({
					next: (base64: string) => {
						if (base64) {
							if (this.path) URL.revokeObjectURL(this.path);
							const byteArray = new Uint8Array(atob(base64).split("").map((c) => c.charCodeAt(0)));
							const blob = new Blob([byteArray], { type: "application/pdf" });
							const newPath = window.URL.createObjectURL(blob);
							this.path = newPath;
							this.fileUrl = this.sanitizer.bypassSecurityTrustResourceUrl(newPath);
							this.name = "IN" + transactionNumber + ".pdf";
							this.invoiceNumber = String(transactionNumber);
						}
					},
					error: () => {}
				});
			},
			error: () => {}
		});
	}

	/**
	 * Check if the order has second home data
	 * Only executes when invoiceMethod is 'D-Cycle'
	 * @author Implemented via AI
	 * @since 2026-01-20
	 */
	private checkOrdersWithSecondHome(): void {
		// Only check for second home if invoiceMethod is D-Cycle
		if (this.invoiceMethodGetterSetter !== 'D-Cycle') {
			this.showSecondHomeButton = false;
			return;
		}

		const filters = {
			division: this.division || '',
			orderNumber: this.orderNumberGetterSetter || '',
			shipDate: '',
			warehouse: '',
			voyageNumber: '',
			vesselName: ''
		};

		this.truckReceiptService.getOrdersWithSecondHome(filters).subscribe(
			(data: any[]) => {
				if (data && data.length > 0) {
					this.ordersWithSecondHome = data;
					this.showSecondHomeButton = true;
				} else {
					this.showSecondHomeButton = false;
				}
			},
			error => {
				this.showSecondHomeButton = false;
			}
		);
	}

	/**
	 * Handle second home button click
	 * @author Implemented via AI
	 * @since 2026-01-20
	 */
	public onSecondHomeClick(): void {
		this.dialogRef.close({ 
			action: 'secondHome', 
			orderNumber: this.orderNumber,
			invoiceNumber: this.invoiceNumber,
			division: this.division,
			gemDBKey: this.gemDBKey
		});
	}

	/**
	 * Set the pdf path and properties to show the pdf in the viewer
	 * @param data base64 string for pdf file
	 * @author Castor Transformación Digital - Alvaro Felipe Garcia Mendez
	 * @since 2020-07-21
	 */
	public setPFD(data: string) {
		this.name = this.fileName;
		if (this.invoiceNumber) {
			// Name for the file
			this.name = "IN" + this.invoiceNumber + ".pdf";
		}
		if (data) {
			// Solo intentar parsear JSON si NO es un Claim y tiene lonNumber
			// Para Claims, los datos vienen como base64 directamente
			if (this.lonNumber != null && this.isLonEditMode === false && !this.isClaim) {
				try {
					const pdfs = JSON.parse(data);
					// Verificar que realmente es un array de objetos (no un string base64)
					if (Array.isArray(pdfs) && pdfs.length > 0 && pdfs[0].pdfBase64) {
						this.pdfList = pdfs.sort((a, b) => b.id - a.id);
						if (this.pdfList.length > 0) {
							this.selectedPdfId = this.pdfList[0].id;
							this.emailToList = this.pdfList[0].emailTO;
							this.emailCcList = this.pdfList[0].emailCC;
							data = this.pdfList[0].pdfBase64;
						} else {
							console.warn("La lista de PDFs está vacía.");
							data = null;
						}
					}
				} catch (e) {
					// Si falla el parseo, asumir que es base64 directo (no es JSON)
					console.warn("Los datos no son JSON válido, se tratarán como base64 directo:", e);
				}
			}

			// Si es un Claim y hay datos de notificación, llenar los campos
			if (this.isClaim && this.notificationData) {
				this.emailToList = this.notificationData.emailsTO || this.notificationData.emailTo || '';
				this.emailCcList = this.notificationData.emailsCC || this.notificationData.emailCc || '';
				// Crear una única entrada en la lista de PDFs para la notificación
				// Los attachments son parte de la notificación, no notificaciones separadas
				const sentDate = this.notificationData.sendDate || this.notificationData.systemDate;
				this.pdfList = [{
					id: this.notificationData.id || 0,
					sentDate: sentDate,
					emailTO: this.emailToList,
					emailCC: this.emailCcList,
					pdfBase64: data
				}];
				this.selectedPdfId = this.pdfList[0].id;
			}

			// decoding the data via atob()
			const byteArray = new Uint8Array(
				atob(data)
					.split("")
					.map((char) => char.charCodeAt(0))
			);
			// Decoded array into blob
			const blob = new Blob([byteArray], { type: "application/pdf" });
			// Here is your URL you can use
			this.path = window.URL.createObjectURL(blob);
			this.fileUrl = this.sanitizer.bypassSecurityTrustResourceUrl(window.URL.createObjectURL(blob));
		}
		
	}

	/**
	 * Determina si la lista de PDFs tiene más de un registro.
	 * @returns `true` si `pdfList` tiene más de un registro, `false` en caso contrario.
	 */
	public hasMultiplePdfs(): boolean {
		return this.pdfList && this.pdfList.length > 1;
	}

	public refreshPdf(value: any) {
		if (this.pdfList && this.pdfList.length > 0) {
			const selectedPdf = this.pdfList.find(pdf => pdf.id === value);
			if (selectedPdf) {
				this.selectedPdfId = selectedPdf.id;
				this.emailToList = selectedPdf.emailTO;
				this.emailCcList = selectedPdf.emailCC;
				const pdfBase64Data = selectedPdf.pdfBase64;

				if (pdfBase64Data) {
					// Revocar la URL del objeto anterior para liberar memoria, si existe
					if (this.path) {
						URL.revokeObjectURL(this.path);
					}

					const byteArray = new Uint8Array(
						atob(pdfBase64Data)
							.split("")
							.map((char) => char.charCodeAt(0))
					);
					const blob = new Blob([byteArray], { type: "application/pdf" });
					const newPdfUrl = window.URL.createObjectURL(blob);
					this.path = newPdfUrl;
					this.fileUrl = this.sanitizer.bypassSecurityTrustResourceUrl(newPdfUrl);
				} else {
					console.warn("El PDF seleccionado no tiene datos base64.");
					this.path = null;
					this.fileUrl = null;
				}
			} else {
				console.warn(`No se encontró el PDF con ID: ${value}`);
			}
		} else {
			console.warn("La lista de PDFs está vacía o no definida.");
		}
	}

	/**
	 * Determina si el botón de cerrar debe mostrarse.
	 * El botón se oculta si `lonNumber` existe.
	 * @returns `true` si el botón de cerrar debe mostrarse, `false` en caso contrario.
	 */
	shouldShowCloseButton(): boolean {
		if (this.isInvoiceSelectionMode) return false;
		return !(this.lonNumber && this.lonNumber !== '');
	}

	downloadPDF() {
		var anchor = document.createElement("a");
		anchor.download = this.invoiceNumber + ".pdf";
		anchor.href = this.path;
		anchor.style.display = "none";
		document.body.appendChild(anchor);
		anchor.click();
	}

	/**
	 * Closes the actual modal
	 * @author Castor Transformación Digital - Alvaro Felipe Garcia Mendez
	 * @since 2019-12-4
	 */
	public close() {
		this.dialogRef.close();
	}
	callBackFn(event) {
		console.log("callBackFn", event);
		// Setting total number of pages
		this.totalPages = event._pdfInfo.numPages;
	}

	/// Functionality to send email with the PDF from C&C - Lon Creation
	sendEmail() {
		this.dialogRef.close(this.lonNumber);
	}

	private showCreditNoteCreateMessageModal(message: string): void {
		const ref = this.dialog.open(ModalAlertComponent, { width: "500px" });
		ref.componentInstance.data = message;
		ref.componentInstance.hasWarning = true;
	}

	/**
	 * Handler for pdf-viewer error event.
	 */
	public onError(event: any): void {}

	/**
	 * Handler for pdf-viewer on-progress event.
	 */
	public onProgress(event: any): void {}
}
